/**
 * Request body schemas shared by the API (validation) and the web app (forms).
 * Every API route validates its input with one of these; unknown fields are
 * rejected so a client can never set columns like firm_id or role by sneaking
 * them into a request.
 */
import { z } from "zod";
import { isRealDate } from "./organizer";
import { MODULES, ROLES } from "./roles";
import { planningSettingsSchema } from "./planning";
import { lifecycleSettingsSchema } from "./lifecycle";
import { cfoDefaultsSchema } from "./finance/benchmarks";

const uuid = z.string().uuid();
const isoDate = z.string().refine(isRealDate, "Enter a real date (YYYY-MM-DD)");
const trimmed = (min: number, max: number) => z.string().trim().min(min).max(max);
/** A fee in cents (up to $999,999.99); null clears it. */
const feeCents = z.number().int().min(0).max(99_999_999).nullable();

export const moduleSchema = z.enum(MODULES);
export const roleSchema = z.enum(ROLES);

// --- Auth ---------------------------------------------------------------

export const PASSWORD_MIN = 12;
export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN, `Use at least ${PASSWORD_MIN} characters`)
  .max(200)
  .refine((p) => /[A-Za-z]/.test(p) && /\d/.test(p), "Use at least one letter and one number");

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(320),
  password: z.string().min(1).max(200),
}).strict();

/** A Clerk session token, exchanged once for a ClientFlow session. */
export const clerkSignInSchema = z.object({ token: z.string().min(20).max(8000) }).strict();

export const mfaCodeSchema = z.object({ code: z.string().regex(/^\d{6}$/, "Enter the 6-digit code") }).strict();

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: passwordSchema,
}).strict();

/** A first password, for an account that has none (signed up with Google). `code` is required with two-step verification. */
export const setPasswordSchema = z.object({
  newPassword: passwordSchema,
  code: z.string().regex(/^\d{6}$/, "Enter the 6-digit code").optional(),
}).strict();

export const forgotPasswordSchema = z.object({ email: z.string().trim().toLowerCase().email("Enter your email address").max(320) }).strict();

export const resetPasswordSchema = z.object({
  token: z.string().min(20).max(200),
  password: passwordSchema,
}).strict();

export const acceptInviteSchema = z.object({
  token: z.string().min(20).max(200),
  name: trimmed(1, 200),
  password: passwordSchema,
}).strict();

export const profileSchema = z.object({ name: trimmed(1, 200) }).strict();

export const switchFirmSchema = z.object({ firmId: uuid }).strict();

// --- Firm members --------------------------------------------------------

export const inviteMemberSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(320),
  name: trimmed(1, 200),
  role: z.enum(["admin", "staff", "readonly"]),
  staffModules: z.array(moduleSchema).max(MODULES.length).default([]),
  hiddenDashboards: z.array(moduleSchema).max(MODULES.length).default([]),
}).strict();

export const updateMemberSchema = z.object({
  role: z.enum(["owner", "admin", "staff", "readonly"]).optional(),
  staffModules: z.array(moduleSchema).max(MODULES.length).optional(),
  // Services whose dashboard this person may not see (the work stays visible).
  hiddenDashboards: z.array(moduleSchema).max(MODULES.length).optional(),
  isActive: z.boolean().optional(),
  // On deactivation: who takes over their clients, open work and open tasks.
  reassignTo: uuid.optional(),
}).strict().refine((v) => v.reassignTo === undefined || v.isActive === false, { message: "Reassign only when deactivating someone" });

// --- Clients -------------------------------------------------------------

export const clientKindSchema = z.enum(["individual", "business"]);
export const clientStatusSchema = z.enum(["prospect", "active", "inactive"]);

export const createClientSchema = z.object({
  kind: clientKindSchema,
  displayName: trimmed(1, 200),
  legalName: trimmed(1, 200).optional(),
  email: z.string().trim().toLowerCase().email().max(320).optional(),
  phone: trimmed(7, 40).optional(),
  mobilePhone: trimmed(7, 40).optional(),
  status: clientStatusSchema.default("active"),
  assignedUserId: uuid.optional(),
  notes: z.string().max(5000).optional(),
  // The client's ID in the tax software (e.g. OLT Pro).
  clientCode: z.string().trim().regex(/^[A-Za-z0-9._-]{1,30}$/, "Letters, digits, dot, dash or underscore (max 30)").optional(),
  modules: z.array(moduleSchema).max(MODULES.length).default([]),
}).strict();

export const updateClientSchema = createClientSchema
  .omit({ modules: true, kind: true })
  .partial()
  .extend({
    legalName: trimmed(1, 200).nullable().optional(),
    email: z.string().trim().toLowerCase().email().max(320).nullable().optional(),
    phone: trimmed(7, 40).nullable().optional(),
    mobilePhone: trimmed(7, 40).nullable().optional(),
    assignedUserId: uuid.nullable().optional(),
    notes: z.string().max(5000).nullable().optional(),
    clientCode: z.string().trim().regex(/^[A-Za-z0-9._-]{1,30}$/, "Letters, digits, dot, dash or underscore (max 30)").nullable().optional(),
    // Dashboards kept out of this client's portal (Financial Reports, Performance Dashboard).
    portalHiddenModules: z.array(z.enum(["bookkeeping", "advisory"])).max(4).transform((v) => [...new Set(v)]).optional(),
  })
  .strict();

export const listClientsQuery = z.object({
  q: z.string().trim().max(100).optional(),
  kind: clientKindSchema.optional(),
  module: moduleSchema.optional(),
  status: z.enum(["prospect", "active", "inactive", "archived"]).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).max(100000).default(0),
}).strict();

/** An owner's percentage of a business, as text: more than 0, up to 100, at most four decimals. */
export const ownershipPctSchema = z
  .string()
  .trim()
  .regex(/^\d{1,3}(\.\d{1,4})?$/, "Enter a percentage such as 50 or 33.3333")
  .refine((v) => Number(v) > 0 && Number(v) <= 100, "Enter a percentage more than 0 and up to 100");

export const relationshipSchema = z.object({
  toClientId: uuid,
  type: z.enum(["spouse", "owner_of", "dependent", "related"]),
  ownershipPct: ownershipPctSchema.nullable().optional(),
}).strict();

export const setServiceSchema = z.object({
  module: moduleSchema,
  level: trimmed(1, 100).optional(),
  // Omitted = keep the current status (or start as active if new).
  status: z.enum(["active", "paused", "ended"]).optional(),
  /** What a recurring plan charges each month. */
  monthlyFeeCents: feeCents.optional(),
}).strict();

export const portalAccessSchema = z.object({ access: z.enum(["full", "documents_only"]) }).strict();

export const invitePortalUserSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(320),
  name: trimmed(1, 200),
  access: z.enum(["full", "documents_only"]).default("full"),
}).strict();

// --- Templates and engagements -------------------------------------------

const stageKey = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/, "lowercase letters, digits and _");

export const templateSchema = z.object({
  module: moduleSchema,
  name: trimmed(1, 200),
  stages: z
    .array(
      z.object({
        key: stageKey,
        name: trimmed(1, 80),
        clientLabel: trimmed(1, 120),
        // Move to the next stage automatically once every request is accepted and organizers are in.
        autoAdvance: z.boolean().optional(),
      }).strict(),
    )
    .min(1)
    .max(30)
    .refine((st) => new Set(st.map((x) => x.key)).size === st.length, "Stage keys must be unique"),
  defaultTasks: z
    .array(z.object({ title: trimmed(1, 300), stageKey: stageKey.optional(), dueInDays: z.number().int().min(0).max(365).optional() }).strict())
    .max(50)
    .default([]),
  defaultRequests: z
    .array(
      z.object({
        type: z.enum(["document", "question", "approval"]),
        title: trimmed(1, 300),
        instructions: z.string().max(2000).optional(),
        dueInDays: z.number().int().min(0).max(365).optional(),
      }).strict(),
    )
    .max(50)
    .default([]),
  /** Tax workflows: the federal return the workflow prepares (sets due dates). */
  returnType: z.enum(["1040", "1065", "1120S", "1120", "1041", "990"]).nullable().optional(),
}).strict();

export const createEngagementSchema = z.object({
  clientId: uuid,
  templateId: uuid,
  name: trimmed(1, 200).optional(),
  period: trimmed(1, 40).optional(),
  assignedUserId: uuid.optional(),
  /** Tax returns: who reviews the preparer's work before the client signs. */
  reviewerUserId: uuid.optional(),
  /** Tax returns of fiscal-year entities: the last month of the tax year (omit for a calendar year). */
  yearEndMonth: z.number().int().min(1).max(11).optional(),
  dueDate: isoDate.optional(),
  /** The form, for a tax workflow that doesn't name one (such as a combined 1120-S / 1065 workflow). */
  returnType: z.enum(["1040", "1065", "1120S", "1120", "1041", "990"]).optional(),
  /** The fee agreed for this work. */
  quotedFeeCents: feeCents.optional(),
}).strict();

export const moveEngagementSchema = z.object({ stageKey }).strict();

/** Reassign an engagement or change its due date; null clears either. */
export const updateEngagementSchema = z
  .object({
    assignedUserId: uuid.nullable().optional(),
    reviewerUserId: uuid.nullable().optional(),
    dueDate: isoDate.nullable().optional(),
    /** Names the form of a tax return that has none yet (it can't be changed once set). */
    returnType: z.enum(["1040", "1065", "1120S", "1120", "1041", "990"]).optional(),
    /** The fee agreed for this work. */
    quotedFeeCents: feeCents.optional(),
  })
  .strict()
  .refine((v) => v.assignedUserId !== undefined || v.reviewerUserId !== undefined || v.dueDate !== undefined || v.returnType !== undefined || v.quotedFeeCents !== undefined, { message: "Nothing to change" });

export const listEngagementsQuery = z.object({
  module: moduleSchema.optional(),
  clientId: uuid.optional(),
  status: z.enum(["open", "completed", "cancelled"]).default("open"),
  limit: z.coerce.number().int().min(1).max(2000).default(1000),
  offset: z.coerce.number().int().min(0).max(1000000).default(0),
  templateId: uuid.optional(),
  stageKey: z.string().trim().min(1).max(100).optional(),
  /** "me", "none" (unassigned) or a staff member's user id. */
  assignee: z.union([z.enum(["me", "none"]), uuid]).optional(),
  due: z.enum(["overdue", "week"]).optional(),
  q: z.string().trim().max(100).optional(),
  /** Board view: at most this many engagements per stage, with true per-stage counts. */
  perStage: z.coerce.number().int().min(1).max(200).optional(),
}).strict();

// --- Tasks ----------------------------------------------------------------

export const createTaskSchema = z.object({
  title: trimmed(1, 300),
  clientId: uuid.optional(),
  engagementId: uuid.optional(),
  assigneeUserId: uuid.optional(),
  dueDate: isoDate.optional(),
}).strict();

export const updateTaskSchema = z.object({
  title: trimmed(1, 300).optional(),
  assigneeUserId: uuid.nullable().optional(),
  dueDate: isoDate.nullable().optional(),
  status: z.enum(["open", "done", "cancelled"]).optional(),
}).strict();

// --- Requests -------------------------------------------------------------

export const createRequestSchema = z.object({
  clientId: uuid,
  engagementId: uuid.optional(),
  type: z.enum(["document", "question", "approval"]),
  title: trimmed(1, 300),
  instructions: z.string().max(2000).optional(),
  dueDate: isoDate.optional(),
  reminderDays: z.array(z.number().int().min(1).max(120)).max(10).optional(),
}).strict();

export const respondRequestSchema = z.object({
  responseText: z.string().trim().max(10000).optional(),
}).strict();

export const reviewRequestSchema = z.object({
  decision: z.enum(["accept", "reopen", "cancel"]),
  // Shown to the client when a request is sent back.
  note: z.string().trim().max(2000).optional(),
}).strict();

// --- Messages -------------------------------------------------------------

export const createThreadSchema = z.object({
  clientId: uuid,
  subject: trimmed(1, 200),
  body: trimmed(1, 20000),
}).strict();

export const postMessageSchema = z.object({
  body: trimmed(1, 20000),
  internal: z.boolean().default(false),
}).strict();

// --- Documents ------------------------------------------------------------

export const uploadDocumentFields = z.object({
  clientId: uuid,
  engagementId: uuid.optional(),
  requestId: uuid.optional(),
  folder: trimmed(1, 120).default("General"),
  // The year folder chosen (multipart fields arrive as text). Omitted: the year it gets by default.
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  visibility: z.enum(["shared", "internal"]).default("shared"),
}).strict();

// --- Billing --------------------------------------------------------------

/** Stripe's single-charge limit is $999,999.99; stay under it. */
export const MAX_INVOICE_CENTS = 99_999_999;

export const invoiceLineSchema = z.object({
  description: trimmed(1, 300),
  quantity: z.number().int().min(1).max(10000),
  unitPriceCents: z.number().int().min(0).max(MAX_INVOICE_CENTS),
}).strict();

export const invoiceSchema = z.object({
  clientId: uuid,
  engagementId: uuid.nullable().optional(),
  memo: z.string().trim().max(2000).optional(),
  dueOn: isoDate.nullable().optional(),
  /** The service billed, when no engagement is linked (a linked engagement's service wins). */
  module: moduleSchema.nullable().optional(),
  lines: z.array(invoiceLineSchema).min(1).max(50),
}).strict();

export const updateInvoiceSchema = invoiceSchema.omit({ clientId: true });

export const listInvoicesQuery = z.object({
  clientId: uuid.optional(),
  status: z.enum(["draft", "open", "paid", "void"]).optional(),
  /** Client name or invoice number. */
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(100),
  offset: z.coerce.number().int().min(0).max(1000000).default(0),
}).strict();

export const manualPaymentSchema = z.object({
  amountCents: z.number().int().min(1).max(MAX_INVOICE_CENTS),
  method: z.enum(["check", "cash", "zelle", "bank_product", "other"]),
  receivedOn: isoDate,
  reference: z.string().trim().max(200).optional(),
}).strict();

export const lockDocumentSchema = z.object({ invoiceId: uuid.nullable() }).strict();
export const engagementBillingSchema = z.object({ bankProduct: z.boolean() }).strict();

// --- E-signatures and consents --------------------------------------------

export const CONSENT_PURPOSES = ["tax_return_use", "tax_return_disclosure", "marketing", "ai_processing", "benchmarking"] as const;
export const consentPurposeSchema = z.enum(CONSENT_PURPOSES);

export const createSignatureSchema = z
  .object({
    clientId: uuid,
    engagementId: uuid.optional(),
    documentId: uuid,
    // irs_8879 covers the whole 8879/8878 family (8879, -CORP, -PE, -S, -F, -EO, 8878).
    kind: z.enum(["simple", "irs_8879"]),
    title: trimmed(1, 200),
    message: z.string().trim().max(2000).optional(),
    signerUserIds: z.array(uuid).min(1).max(4),
    // Boxes placed on the PDF. Positions are fractions of the visible page, from its top-left corner.
    fields: z
      .array(
        z.object({
          signerUserId: uuid,
          page: z.number().int().min(0).max(1999),
          x: z.number().min(0).max(1),
          y: z.number().min(0).max(1),
          width: z.number().min(0.01).max(1),
          height: z.number().min(0.005).max(1),
          kind: z.enum(["signature", "initials", "date", "text"]),
          label: z.string().trim().max(60).optional(),
          required: z.boolean().default(true),
        }).strict(),
      )
      .max(100)
      .default([]),
    dueOn: isoDate.optional(),
    consentPurpose: consentPurposeSchema.optional(),
    consentExpiresOn: isoDate.optional(),
  })
  .strict()
  .refine((v) => new Set(v.signerUserIds).size === v.signerUserIds.length, "Each signer can be listed once")
  .refine((v) => !v.consentPurpose || v.kind === "simple", "Consent forms use the standard signing type")
  .refine((v) => !v.consentExpiresOn || v.consentPurpose, "An expiry date only applies to consent forms")
  .refine((v) => v.fields.every((f) => v.signerUserIds.includes(f.signerUserId)), "Every box must belong to one of the signers")
  .refine((v) => v.fields.every((f) => f.x + f.width <= 1.0001 && f.y + f.height <= 1.0001), "A box runs off the page")
  .refine((v) => v.fields.length === 0 || v.signerUserIds.every((id) => v.fields.some((f) => f.signerUserId === id && f.kind === "signature")), "Give every signer at least one signature box");

export const signSchema = z.object({
  typedName: trimmed(1, 200),
  // Answers for the signer's text boxes, by box id.
  values: z.record(uuid, z.string().trim().max(200)).default({}),
  agree: z.literal(true, { errorMap: () => ({ message: "Check the box to agree to sign electronically" }) }),
}).strict();

export const declineSignatureSchema = z.object({ reason: z.string().trim().max(2000).optional() }).strict();

export const reviewSignatureSchema = z.object({
  decision: z.enum(["accept", "reopen"]),
  note: z.string().trim().max(2000).optional(),
}).strict();

export const recordConsentSchema = z.object({
  clientId: uuid,
  purpose: consentPurposeSchema,
  grantedOn: isoDate,
  expiresOn: isoDate.nullable().optional(),
  signerName: trimmed(1, 200),
  documentId: uuid.optional(),
}).strict();

// --- Tax season -------------------------------------------------------------

export const bulkEngagementSchema = z.discriminatedUnion("action", [
  z.object({ action: z.literal("move"), ids: z.array(uuid).min(1).max(500), stageKey }).strict(),
  z.object({ action: z.literal("assign"), ids: z.array(uuid).min(1).max(500), assignedUserId: uuid.nullable() }).strict(),
]);

/** Who a bulk organizer send reaches: active tax clients matching a search, optionally only those with no organizer for the year yet. */
export const organizerRecipientsFilter = z.object({
  q: z.string().trim().max(100).optional(),
  notSent: z.boolean().optional(),
}).strict();

/** Either the chosen clients, or every active tax client matching `allMatching`. */
export const bulkOrganizerSchema = z.object({
  clientIds: z.array(uuid).min(1).max(1000).optional(),
  allMatching: organizerRecipientsFilter.optional(),
  templateId: uuid,
  taxYear: z.number().int().min(2000).max(2100),
}).strict().refine((b) => (b.clientIds ? 1 : 0) + (b.allMatching ? 1 : 0) === 1, { message: "Choose the clients, or send to all matching clients" });

const cents = z.number().int().min(0).max(99_999_999_999);

export const createFilingSchema = z.object({
  engagementId: uuid,
  taxYear: z.number().int().min(2000).max(2100),
  jurisdiction: z.string().regex(/^(federal|[A-Z]{2})$/),
  form: trimmed(1, 30),
  isAmended: z.boolean().default(false),
}).strict();

export const updateFilingSchema = z.object({
  filingStatus: z.enum(["single", "mfj", "mfs", "hoh", "qss"]).nullable().optional(),
  filingMethod: z.enum(["efile", "paper"]).optional(),
  extensionFiledOn: isoDate.nullable().optional(),
  filedOn: isoDate.nullable().optional(),
  acceptedOn: isoDate.nullable().optional(),
  rejectedOn: isoDate.nullable().optional(),
  refundCents: cents.nullable().optional(),
  balanceDueCents: cents.nullable().optional(),
  refundMethod: z.enum(["direct_deposit", "check", "apply_next_year", "bank_product"]).nullable().optional(),
  refundReceivedOn: isoDate.nullable().optional(),
}).strict();

export const firmSettingsSchema = z.object({
  timeZone: z.string().max(60).optional(),
  // Owner's choice: let clients click-sign IRS Form 8879 without the identity check
  // IRS Publication 1345 requires for remote signing. The firm carries that risk.
  allow8879WithoutIdentityCheck: z.boolean().optional(),
  // Refund-transfer partner shown to bank-product clients on their refund card.
  bankProduct: z.object({ name: trimmed(1, 100), trackerUrl: z.string().url().startsWith("https://").max(300) }).strict().nullable().optional(),
  // The optional time tracker on engagements (feeds Firm Analytics).
  timeTracking: z.boolean().optional(),
  // Tax planning tiers, whether CFO plans include it, and when clients are suggested for it.
  taxPlanning: planningSettingsSchema.optional(),
  // Proposals, engagement letter terms, recurring billing, renewals and review requests.
  clientLifecycle: lifecycleSettingsSchema.optional(),
  // CFO defaults per industry: KPI targets, and published benchmark references with their source.
  cfoDefaults: cfoDefaultsSchema.optional(),
  // Staff (by user id) the owner lets refund, credit, adjust and void invoices. Owners and admins always can.
  billingAdjusters: z.array(uuid).max(100).optional(),
}).strict();

export type CreateClientInput = z.infer<typeof createClientSchema>;
export type TemplateInput = z.infer<typeof templateSchema>;
